import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileSignature,
  Download,
  CheckCircle2,
  Clock,
  PenTool,
  Search,
  User,
  Building2,
  Loader2,
  X,
  Trash2,
  Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminEmploymentContracts() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContract, setSelectedContract] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [signing, setSigning] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, [statusFilter]);

  const fetchContracts = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/admin/employment-contracts${params}`);
      setContracts(res.data.contracts || []);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  // Canvas functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  useEffect(() => {
    if (showSignModal && canvasRef.current) {
      setTimeout(initCanvas, 100);
    }
  }, [showSignModal]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
    setHasSignature(false);
  };

  const handleHRSign = async () => {
    if (!hasSignature || !selectedContract) {
      toast.error("Please draw your signature first");
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureImage = canvas.toDataURL("image/png");
      
      await axios.post(`${API}/employment-contracts/${selectedContract.contract_id}/hr-sign`, {
        signature_image: signatureImage
      });
      
      toast.success("Contract signed by HR successfully!");
      setShowSignModal(false);
      setSelectedContract(null);
      setHasSignature(false);
      fetchContracts();
    } catch (err) {
      console.error("Sign error:", err);
      toast.error(err.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadPDF = async (contractId) => {
    try {
      const response = await axios.get(
        `${API}/employment-contracts/${contractId}/pdf`,
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `employment_contract_${contractId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Contract downloaded!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download contract");
    }
  };

  const handleRejectContract = async () => {
    if (!selectedContract) return;
    
    setRejecting(true);
    try {
      await axios.post(`${API}/employment-contracts/${selectedContract.contract_id}/reject`, {
        reason: rejectReason
      });
      
      toast.success("Contract rejected");
      setShowRejectModal(false);
      setSelectedContract(null);
      setRejectReason("");
      fetchContracts();
    } catch (err) {
      console.error("Reject error:", err);
      toast.error(err.response?.data?.detail || "Failed to reject contract");
    } finally {
      setRejecting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">Pending</Badge>;
      case "worker_signed":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">Worker Signed</Badge>;
      case "hr_signed":
        return <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30">HR Signed</Badge>;
      case "fully_signed":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">Fully Signed</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredContracts = contracts.filter(c => 
    c.worker_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.worker_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contract_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    total: contracts.length,
    pending: contracts.filter(c => c.status === "pending").length,
    workerSigned: contracts.filter(c => c.status === "worker_signed").length,
    fullySigned: contracts.filter(c => c.status === "fully_signed").length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-contracts-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Employment Contracts</h1>
        <p className="text-[#94A3B8]">Manage worker employment agreements</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-[#94A3B8]">Total Contracts</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-[#94A3B8]">Pending</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-[#94A3B8]">Awaiting HR</p>
            <p className="text-2xl font-bold text-blue-500">{stats.workerSigned}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-[#94A3B8]">Fully Signed</p>
            <p className="text-2xl font-bold text-green-500">{stats.fullySigned}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#0F0F0F] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                placeholder="Search by name, email, or contract ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="flex gap-2">
              {["all", "pending", "worker_signed", "fully_signed", "rejected"].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status 
                    ? "bg-[#23F7DD] text-[#050505]" 
                    : "border-white/20 text-white hover:bg-white/10"
                  }
                >
                  {status === "all" ? "All" : status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card className="bg-[#0F0F0F] border-white/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-[#94A3B8]">Worker</TableHead>
                <TableHead className="text-[#94A3B8]">Contract ID</TableHead>
                <TableHead className="text-[#94A3B8]">Status</TableHead>
                <TableHead className="text-[#94A3B8]">Worker Signed</TableHead>
                <TableHead className="text-[#94A3B8]">HR Signed</TableHead>
                <TableHead className="text-[#94A3B8]">Created</TableHead>
                <TableHead className="text-[#94A3B8] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#94A3B8]">
                    No contracts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredContracts.map((contract) => (
                  <TableRow key={contract.contract_id} className="border-white/10">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{contract.worker_name}</p>
                        <p className="text-sm text-[#94A3B8]">{contract.worker_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-[#94A3B8]">
                      {contract.contract_id}
                    </TableCell>
                    <TableCell>{getStatusBadge(contract.status)}</TableCell>
                    <TableCell>
                      {contract.worker_signed ? (
                        <span className="text-green-500 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          {new Date(contract.worker_signed_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8] flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contract.hr_signed ? (
                        <span className="text-green-500 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          {contract.hr_signed_by_name}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8] flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#94A3B8]">
                      {new Date(contract.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* Sign button - only for worker_signed contracts */}
                        {!contract.hr_signed && contract.worker_signed && contract.status !== "rejected" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowSignModal(true);
                            }}
                            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                            data-testid={`sign-btn-${contract.contract_id}`}
                          >
                            <PenTool className="w-4 h-4 mr-1" />
                            Sign
                          </Button>
                        )}
                        
                        {/* Reject button - for pending or worker_signed contracts */}
                        {contract.status !== "fully_signed" && contract.status !== "rejected" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowRejectModal(true);
                            }}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid={`reject-btn-${contract.contract_id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Download PDF - only for fully signed */}
                        {contract.status === "fully_signed" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPDF(contract.contract_id)}
                            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            className="border-white/10 text-[#94A3B8] cursor-not-allowed"
                            title="PDF available after both signatures"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* HR Sign Modal */}
      {showSignModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#0F0F0F] border-white/10 w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Sign Employment Contract</CardTitle>
                  <CardDescription>Worker: {selectedContract.worker_name}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowSignModal(false);
                    setSelectedContract(null);
                    setHasSignature(false);
                  }}
                  className="text-[#94A3B8] hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contract Summary */}
              <div className="p-4 bg-white/5 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Contract ID:</span>
                  <span className="text-white font-mono">{selectedContract.contract_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Position:</span>
                  <span className="text-white">{selectedContract.position}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Worker Signed:</span>
                  <span className="text-green-500">
                    {new Date(selectedContract.worker_signed_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Signature Canvas */}
              <div>
                <p className="text-sm text-[#94A3B8] mb-2">Your signature (HR/Admin):</p>
                <div className="border-2 border-dashed border-white/20 rounded-lg p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <Button
                  variant="ghost"
                  onClick={clearSignature}
                  className="text-[#94A3B8] hover:text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear
                </Button>
                <p className="text-sm text-[#94A3B8]">
                  {hasSignature ? "✓ Signature drawn" : "Draw your signature above"}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSignModal(false);
                    setSelectedContract(null);
                    setHasSignature(false);
                  }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleHRSign}
                  disabled={!hasSignature || signing}
                  className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] disabled:opacity-50"
                  data-testid="confirm-hr-sign-btn"
                >
                  {signing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Sign as HR
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reject Contract Modal */}
      {showRejectModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#0F0F0F] border-white/10 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <X className="w-5 h-5 text-red-400" />
                    Reject Contract
                  </CardTitle>
                  <CardDescription>Worker: {selectedContract.worker_name}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedContract(null);
                    setRejectReason("");
                  }}
                  className="text-[#94A3B8] hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-red-400 text-sm">
                  ⚠️ This action will reject the employment contract. The worker will be notified and will need to create a new contract if they wish to continue.
                </p>
              </div>

              <div className="p-4 bg-white/5 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Contract ID:</span>
                  <span className="text-white font-mono">{selectedContract.contract_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Email:</span>
                  <span className="text-white">{selectedContract.worker_email}</span>
                </div>
              </div>

              <div>
                <label className="text-sm text-[#94A3B8] mb-2 block">
                  Rejection Reason (optional)
                </label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Missing documents, incorrect information..."
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedContract(null);
                    setRejectReason("");
                  }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRejectContract}
                  disabled={rejecting}
                  className="flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                  data-testid="confirm-reject-btn"
                >
                  {rejecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Reject Contract
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
